<template>
  <section ref="root" class="hero">
    <div ref="media" class="video-container">
      <video ref="video" muted loop playsinline preload="auto">
        <source src="/conceptualComprimido.webm" type="video/webm">
        <source src="/conceptualComprimido.mp4" type="video/mp4">
      </video>
    </div>

    <div v-if="!introDone" ref="intro" class="intro" aria-hidden="true">
      <div ref="left" class="intro__half intro__half--left">
        <span class="intro__mask"><span class="intro__tag" data-intro-tag>Hecho en Rosario</span></span>
      </div>
      <div ref="right" class="intro__half intro__half--right">
        <span class="intro__mask"><span class="intro__tag" data-intro-tag>Para gente coppada</span></span>
      </div>

      <div ref="seam" class="intro__seam">
        <div ref="fill" class="intro__seam-fill"></div>
      </div>

      <div class="intro__ui">
        <span class="intro__meta intro__meta--tl" data-intro-meta>(Coppados)</span>
        <span class="intro__meta intro__meta--tr" data-intro-meta>Rosario, AR — 32°57′S 60°40′W</span>
        <span class="intro__counter">
          <span ref="counter" class="intro__counter-num">000</span>
        </span>
        <span class="intro__meta intro__meta--br" data-intro-meta>Sound on for the full experience</span>
      </div>
    </div>

    <div ref="volume" class="volumen-container">
      <button class="video-button" type="button" @click="toggleSound">
        <img src="../assets/volumen.webp" alt="">
        <span>{{ muted ? 'Sound On' : 'Sound Off' }}</span>
      </button>
    </div>
  </section>
</template>

<script>
import { gsap, prefersReducedMotion } from '@/animations/gsap'
import { lockScroll, unlockScroll, scrollToTop } from '@/animations/smoothScroll'
import motion from '@/mixins/motion'

// La intro se ve una sola vez por visita (al volver desde /shop no se repite).
let introPlayed = false

const MAX_WAIT = 4500 // ms máximos esperando el video antes de abrir igual

export default {
  name: 'WelcomeView',
  mixins: [motion],
  data() {
    return {
      introDone: introPlayed,
      muted: true
    }
  },
  mounted() {
    if (introPlayed || prefersReducedMotion()) {
      this.skipIntro()
    } else {
      this.startIntro()
    }
    this.motion(() => {
      if (prefersReducedMotion()) return
      // Salida limpia: el video se queda atrás mientras la página sube.
      gsap.to(this.$refs.media, {
        yPercent: 30,
        ease: 'none',
        scrollTrigger: { trigger: this.$refs.root, start: 'top top', end: 'bottom top', scrub: true }
      })
      gsap.to(this.$refs.volume, {
        opacity: 0,
        ease: 'none',
        scrollTrigger: { trigger: this.$refs.root, start: 'top top', end: '25% top', scrub: true }
      })
    })
  },
  beforeDestroy() {
    this.destroyed = true
    clearTimeout(this.waitTimer)
    const timelines = [this.loadTimeline, this.progressTween, this.introTimeline]
    timelines.forEach((t) => t && t.kill())
    if (this.locked) unlockScroll()
  },
  methods: {
    playVideo() {
      const video = this.$refs.video
      if (!video) return
      const playing = video.play()
      if (playing && playing.catch) playing.catch(() => {})
    },
    toggleSound() {
      const video = this.$refs.video
      video.muted = !video.muted
      this.muted = video.muted
    },
    skipIntro() {
      introPlayed = true
      this.introDone = true
      gsap.set(this.$refs.volume, { opacity: 1 })
      this.playVideo()
    },
    // Se resuelve cuando el video puede reproducirse (o al llegar al tope de espera).
    videoReady() {
      const video = this.$refs.video
      return new Promise((resolve) => {
        if (!video || video.readyState >= 3) {
          resolve()
          return
        }
        const done = () => {
          video.removeEventListener('canplaythrough', done)
          video.removeEventListener('canplay', done)
          clearTimeout(this.waitTimer)
          resolve()
        }
        video.addEventListener('canplaythrough', done)
        video.addEventListener('canplay', done)
        this.waitTimer = setTimeout(done, MAX_WAIT)
      })
    },
    startIntro() {
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual'
      scrollToTop()
      lockScroll()
      this.locked = true

      const intro = this.$refs.intro
      const tags = intro.querySelectorAll('[data-intro-tag]')
      const meta = intro.querySelectorAll('[data-intro-meta]')
      const navItems = document.querySelectorAll('[data-nav-in]')
      const counter = { value: 0 }
      const render = () => {
        this.$refs.counter.textContent = String(Math.round(counter.value)).padStart(3, '0')
        this.$refs.fill.style.transform = `scaleY(${counter.value / 100})`
      }

      gsap.set(navItems, { opacity: 0, y: -16 })
      gsap.set(this.$refs.volume, { opacity: 0 })
      gsap.set(this.$refs.video, { scale: 1.3 })

      // 1. Entrada: la costura aparece, las frases suben, cuenta hasta ~86.
      this.loadTimeline = gsap.timeline()
        .fromTo(this.$refs.seam, { scaleY: 0 }, { scaleY: 0.22, duration: 1, ease: 'expo.inOut' }, 0.1)
        .from(tags, { yPercent: 110, duration: 1.2, stagger: 0.12, ease: 'expo.out' }, 0.45)
        .from(meta, { y: 14, opacity: 0, duration: 1, stagger: 0.07, ease: 'expo.out' }, 0.5)
        .from(this.$refs.counter, { yPercent: 110, duration: 1.1, ease: 'expo.out' }, 0.3)
        .to(counter, { value: 86, duration: 1.8, ease: 'power2.out', onUpdate: render }, 0.3)

      // 2. Espera real al video; 3. completa a 100 y abre.
      Promise.all([this.loadTimeline.then(), this.videoReady()]).then(() => {
        if (this.destroyed) return
        this.progressTween = gsap.to(counter, {
          value: 100,
          duration: 0.55,
          ease: 'power2.inOut',
          onUpdate: render,
          onComplete: () => this.openIntro(navItems)
        })
      })
    },
    openIntro(navItems) {
      if (this.destroyed) return
      const { left, right, seam, counter, video, volume } = this.$refs
      const meta = this.$refs.intro.querySelectorAll('[data-intro-meta]')

      this.introTimeline = gsap.timeline()
        .to(counter, { yPercent: -110, duration: 0.6, ease: 'power3.in' }, 0.1)
        .to(meta, { y: -12, opacity: 0, duration: 0.45, stagger: 0.04, ease: 'power2.in' }, 0.1)
        // La costura se estira a todo el alto: el "corte".
        .to(seam, { scaleY: 1, duration: 0.75, ease: 'expo.inOut' }, 0.15)
        .add(this.playVideo, 0.6)
        .to(left, { xPercent: -100, duration: 1.5, ease: 'expo.inOut' }, 0.7)
        .to(right, { xPercent: 100, duration: 1.5, ease: 'expo.inOut' }, 0.7)
        .to(seam, { opacity: 0, duration: 0.35, ease: 'power2.out' }, 0.75)
        .to(video, { scale: 1, duration: 2.6, ease: 'expo.out' }, 0.75)
        .to(navItems, { opacity: 1, y: 0, duration: 1.1, stagger: 0.07, ease: 'expo.out', clearProps: 'opacity,transform' }, 1.5)
        .to(volume, { opacity: 1, duration: 1.2, ease: 'power2.out' }, 1.7)
        .add(() => {
          introPlayed = true
          this.introDone = true
          this.locked = false
          unlockScroll()
        }, 2.25)
    }
  }
}
</script>

<style scoped>
.hero {
  position: relative;
  width: 100%;
  height: 100vh;
  height: 100svh;
  overflow: hidden;
  background: #000;
}

.video-container {
  position: absolute;
  inset: 0;
  overflow: hidden;
  will-change: transform;
}

.video-container video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.volumen-container {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  opacity: 0;
}

.video-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  transition: color 0.35s ease-in-out;
}

.video-button img {
  width: 1rem;
  height: 1rem;
  filter: invert(1);
}

@media (hover: hover) {
  .video-button:hover {
    color: var(--c-accent);
  }
}

/* ---------- Intro ---------- */

.intro {
  position: absolute;
  inset: 0;
  z-index: 3;
  color: #fff;
  pointer-events: none;
}

.intro__half {
  position: absolute;
  top: 0;
  width: 50%;
  height: 100%;
  background: var(--c-accent);
  display: flex;
  align-items: center;
  will-change: transform;
}

.intro__half--left {
  left: 0;
  justify-content: flex-end;
  text-align: right;
}

.intro__half--right {
  right: 0;
  justify-content: flex-start;
}

.intro__mask {
  display: block;
  overflow: hidden;
  padding: 0 clamp(1.25rem, 0.5rem + 2.5vw, 3.5rem);
}

.intro__tag {
  display: block;
  font-size: clamp(0.72rem, 0.6rem + 0.45vw, 1.05rem);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  line-height: 1.3;
}

.intro__seam {
  position: absolute;
  top: 0;
  left: 50%;
  width: 2px;
  height: 100%;
  margin-left: -1px;
  background: rgba(255, 255, 255, 0.28);
  transform: scaleY(0.22);
  overflow: hidden;
}

.intro__seam-fill {
  position: absolute;
  inset: 0;
  background: #fff;
  transform: scaleY(0);
  transform-origin: top;
}

.intro__ui {
  position: absolute;
  inset: 0;
  padding: calc(var(--header-h) * 0.35) var(--gutter);
}

.intro__meta {
  position: absolute;
  font-size: var(--fs-small);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.85;
}

.intro__meta--tl {
  top: 1.5rem;
  left: var(--gutter);
}

.intro__meta--tr {
  top: 1.5rem;
  right: var(--gutter);
}

.intro__meta--br {
  bottom: 1.6rem;
  right: var(--gutter);
  max-width: 14rem;
  text-align: right;
}

.intro__counter {
  position: absolute;
  left: var(--gutter);
  bottom: 0.6rem;
  display: block;
  overflow: hidden;
  line-height: 0.82;
}

.intro__counter-num {
  display: block;
  font-size: clamp(4.5rem, 2rem + 11vw, 13rem);
  letter-spacing: -0.04em;
  font-variant-numeric: tabular-nums;
  padding-top: 0.06em;
}

@media (max-width: 600px) {
  .intro__meta--tr {
    max-width: 9rem;
    text-align: right;
  }

  .intro__meta--br {
    max-width: 9rem;
  }
}
</style>
